/**
 * Pricing Page - Membership Plans Comparison
 * Shows three membership plans with features and upgrade buttons
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DesktopLayout from '../../components/layout/DesktopLayout';
import ProtectedRoute from '../../components/auth/ProtectedRoute';
import './PricingPage.css';

interface Plan {
  id: 'free' | 'basic' | 'premium';
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}

const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: '免費',
    period: '永久免費',
    description: '適合剛開始的用戶',
    features: [
      '最多 3 則廣告',
      '最多 10 次聯繫',
      '基本搜尋功能',
      '瀏覽許願池',
    ],
    cta: '目前方案',
  },
  {
    id: 'basic',
    name: 'Basic',
    price: 'NT$299',
    period: '每月',
    description: '適合小型商家',
    features: [
      '最多 10 則廣告',
      '最多 50 次聯繫',
      '進階搜尋功能',
      '優先展示',
      'Email 通知',
    ],
    cta: '升級到 Basic',
    highlighted: true,
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 'NT$899',
    period: '每月',
    description: '適合專業商家',
    features: [
      '無限廣告數量',
      '無限聯繫次數',
      '所有搜尋功能',
      '頂級展示',
      '優先客戶支援',
      // 'LINE 推播通知',
      '數據分析報表',
    ],
    cta: '升級到 Premium',
  },
];

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);

  const currentPlan = user?.plan || 'free';

  const handleUpgrade = async (planId: 'basic' | 'premium') => {
    if (planId === currentPlan) {
      return; // Already on this plan
    }

    setIsProcessing(true);

    try {
      // TODO: Integrate with payment gateway
      // For now, just show an alert
      alert(`升級到 ${planId.toUpperCase()} 方案的功能將在未來版本中實作。`);
    } catch (error) {
      console.error('Upgrade failed:', error);
      alert('升級失敗，請稍後再試');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePlanClick = (planId: 'free' | 'basic' | 'premium') => {
    if (planId === 'free') {
      navigate('/member');
      return;
    }

    handleUpgrade(planId);
  };

  return (
    <div className="pricing-page">
      <DesktopLayout>
      <div className="pricing-page__container">
        {/* Header */}
        <div className="pricing-page__header">
          <button
            onClick={() => navigate(-1)}
            className="pricing-page__back-btn"
          >
            ← 返回
          </button>
          <h1 className="pricing-page__title">選擇您的會員方案</h1>
          <p className="pricing-page__subtitle">
            找到最適合您需求的方案，隨時可以升級或降級
          </p>
        </div>

        {/* Current Plan Banner */}
        {currentPlan !== 'free' && (
          <div className="pricing-page__current-plan">
            目前方案：{currentPlan === 'basic' ? 'Basic' : 'Premium'}
          </div>
        )}

        {/* Plans Grid */}
        <div className="pricing-page__plans">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`pricing-plan ${
                plan.highlighted ? 'pricing-plan--highlighted' : ''
              } ${
                plan.id === currentPlan ? 'pricing-plan--current' : ''
              }`}
            >
              {plan.highlighted && (
                <div className="pricing-plan__badge">推薦</div>
              )}

              <div className="pricing-plan__content">
                <h2 className="pricing-plan__name">{plan.name}</h2>
                <div className="pricing-plan__price">
                  <span className="pricing-plan__amount">{plan.price}</span>
                  {plan.period !== '永久免費' && (
                    <span className="pricing-plan__period">/{plan.period}</span>
                  )}
                </div>
                <p className="pricing-plan__description">{plan.description}</p>

                <ul className="pricing-plan__features">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="pricing-plan__feature">
                      <span className="pricing-plan__check">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePlanClick(plan.id as 'free' | 'basic' | 'premium')}
                  disabled={
                    isProcessing ||
                    (plan.id === 'free' && currentPlan === 'free') ||
                    (plan.id === currentPlan)
                  }
                  className={`pricing-plan__cta ${
                    plan.highlighted ? 'pricing-plan__cta--primary' : ''
                  } ${
                    plan.id === currentPlan ? 'pricing-plan__cta--current' : ''
                  }`}
                >
                  {plan.id === currentPlan
                    ? '目前方案'
                    : plan.id === 'free'
                    ? '返回會員中心'
                    : plan.cta}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="pricing-page__faq">
          <h2 className="pricing-page__faq-title">常見問題</h2>

          <div className="pricing-faq-item">
            <h3 className="pricing-faq-item__question">
              如何升級或降級我的方案？
            </h3>
            <p className="pricing-faq-item__answer">
              您可以隨時在會員中心升級或降級您的方案。升級會立即生效，
              降級則會在當前計費週期結束後生效。
            </p>
          </div>

          <div className="pricing-faq-item">
            <h3 className="pricing-faq-item__question">
              可以隨時取消訂閱嗎？
            </h3>
            <p className="pricing-faq-item__answer">
              是的，您可以隨時取消訂閱。取消後您仍可使用該方案直到當前計費週期結束。
            </p>
          </div>

          <div className="pricing-faq-item">
            <h3 className="pricing-faq-item__question">
              付款方式有哪些？
            </h3>
            <p className="pricing-faq-item__answer">
              我們支援信用卡、LINE Pay、以及銀行轉帳等多種付款方式。
            </p>
          </div>
        </div>

        {/* Contact Support */}
        <div className="pricing-page__support">
          <p>
            需要協助？
            <button
              onClick={() => navigate('/help')}
              className="pricing-page__support-link"
            >
              聯繫客服支援
            </button>
          </p>
        </div>
      </div>
      </DesktopLayout>
    </div>
  );
};

// Wrap with ProtectedRoute
export default () => (
  <ProtectedRoute>
    <PricingPage />
  </ProtectedRoute>
);
